import { DatePicker, Form, Input, Modal, Typography, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import { apiErrorMessage, useParkWithDate } from "./api";
import { formatDay, parkReason, parkRequest, UkhoDialogTarget } from "./lib";
import {
  DEACTIVATED_VESSELS_SHIPCO_NAME,
  DeactivatedVesselsLookupStatus,
  useDeactivatedVesselsLookup,
} from "./useDeactivatedVesselsShipcoId";

/** What the To row says while the Deactivated Vessels shipco is not known. */
const LOOKUP_HELP_TEXT: Record<
  Exclude<DeactivatedVesselsLookupStatus, "found">,
  string
> = {
  pending: "Looking up Deactivated Vessels",
  failed: "Could not look up Deactivated Vessels",
  missing: "Deactivated Vessels customer not found",
};

export interface ParkWithDateModalProps {
  open: boolean;
  /** The vessel and the UKHO leaving notice (a work-list row or the vessel page's view). */
  target: UkhoDialogTarget;
  onClose: () => void;
  /** Called after the park succeeded and its confirmation was shown. */
  onDone: () => void;
}

/**
 * Spec 11.2 park dialog: the existing park (POST /vessel-transfer,
 * to_old_vessels) with the effective date and the reason taken from the UKHO
 * notice. Both stay editable.
 */
export const ParkWithDateModal: React.FC<ParkWithDateModalProps> = ({
  open,
  target,
  onClose,
  onDone,
}) => {
  const { shipcoId: deactivatedShipcoId, status: lookupStatus } =
    useDeactivatedVesselsLookup();
  // Park stays disabled until the shipco is known; the To row says why.
  const lookupHelp =
    lookupStatus === "found" ? undefined : LOOKUP_HELP_TEXT[lookupStatus];
  const { mutate: park, mutation } = useParkWithDate();
  const [effectiveDay, setEffectiveDay] = useState<Dayjs>(() =>
    dayjs(target.day)
  );
  const [reason, setReason] = useState(() =>
    parkReason(target.day, target.transferId)
  );

  // Each opening starts from the notice's day and reason.
  useEffect(() => {
    if (open) {
      setEffectiveDay(dayjs(target.day));
      setReason(parkReason(target.day, target.transferId));
    }
  }, [open, target.day, target.transferId]);

  const submitPark = () => {
    if (deactivatedShipcoId === null) {
      return;
    }
    const effectiveDate = effectiveDay.format("YYYY-MM-DD");
    park(
      parkRequest(target, deactivatedShipcoId, effectiveDate, reason.trim()),
      {
        onSuccess: () => {
          message.success(
            `${target.vesselName} parked with effective date ${formatDay(effectiveDate)}`
          );
          onDone();
        },
        onError: (error) => {
          message.error(apiErrorMessage(error));
        },
      }
    );
  };

  return (
    <Modal
      open={open}
      title={`Park ${target.vesselName}`}
      okText="Park"
      cancelText="Cancel"
      onOk={submitPark}
      onCancel={onClose}
      okButtonProps={{ disabled: deactivatedShipcoId === null }}
      confirmLoading={mutation.isPending}
      width={560}
    >
      <Typography.Paragraph type="secondary">
        The same park as on the Vessel Transfers screen, with the date and
        reason filled in from the UKHO notice.
      </Typography.Paragraph>
      <Form layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
        <Form.Item label="Ship" style={{ marginBottom: 8 }}>
          <Typography.Text strong>{target.vesselName}</Typography.Text>{" "}
          <Typography.Text type="secondary">{target.imoNumber}</Typography.Text>
        </Form.Item>
        <Form.Item label="From" style={{ marginBottom: 8 }}>
          {target.customer ?? ""}
        </Form.Item>
        <Form.Item label="To" style={{ marginBottom: 8 }} extra={lookupHelp}>
          {DEACTIVATED_VESSELS_SHIPCO_NAME}
        </Form.Item>
        <Form.Item label="Effective date" extra="UKHO leaving day">
          <DatePicker
            value={effectiveDay}
            onChange={(day) => day && setEffectiveDay(day)}
            allowClear={false}
            format="D MMM YYYY"
          />
        </Form.Item>
        <Form.Item label="Reason">
          <Input.TextArea
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
